/**
 * The four filter control shapes. All of them multi-select: the brief says
 * "filter companies under sector, then industry", and nothing about that
 * requires single choice -- `--symbol` alongside `--sector` narrows in the
 * pipeline's own CLI, and this matches that reading (§7.1).
 */
import type { ReactNode } from 'react'
import { Bar, Chip, Label, bandOf } from '~/components/ui/primitives.tsx'
import { Raw } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'
import { useT } from '~/i18n/index.tsx'
import { percent0 } from '~/i18n/format.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'

export function Group({
  label, hint, aside, children, muted, className,
}: {
  label: ReactNode
  hint?: ReactNode
  aside?: ReactNode
  children: ReactNode
  /** A level with exactly one option: present, auto-selected, de-emphasised. */
  muted?: boolean
  className?: string
}) {
  return (
    <section className={cn('py-3', muted && 'opacity-60', className)}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <Label>{label}</Label>
        {aside && (
          <div className="text-[length:var(--text-micro)] text-[var(--text-faint)]">{aside}</div>
        )}
      </div>
      {children}
      {hint && (
        <p className="mt-1.5 max-w-[64ch] text-[length:var(--text-tiny)] leading-[var(--text-tiny--line-height)] text-[var(--text-faint)]">
          {hint}
        </p>
      )}
    </section>
  )
}

export type ChipOption = {
  value: string
  label: ReactNode
  documents: number
  available: boolean
}

export function ChipRow({
  options, selected, onToggle,
}: {
  options: ChipOption[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <Chip
          key={o.value}
          selected={selected.includes(o.value)}
          unavailable={!o.available}
          count={o.documents}
          onClick={() => onToggle(o.value)}
        >
          {o.label}
        </Chip>
      ))}
    </div>
  )
}

/**
 * "Select all" and "None" were 10.5px text links with no padding -- about a
 * 40 × 14px target for the two most destructive controls on the screen. They
 * keep the quiet link styling and gain a real box around it.
 */
const BULK_ACTION =
  'relative tap inline-flex min-h-[var(--control-h-sm)] items-center rounded-[var(--radius-control)] ' +
  'px-1.5 text-[length:var(--text-micro)] text-[var(--accent)] transition-colors duration-75 ' +
  'hover:bg-[var(--bg-raised)] hover:underline active:bg-[var(--bg-raised)]'

export type CompanyOption = {
  symbol: string
  nameEn: string | null
  nameAr: string | null
  documents: number
  passRate: number | null
}

/**
 * The company list. The bar on each row is that company's validation pass
 * rate, so the quality of what you are about to open is visible before you
 * open it (§10.1).
 */
export function CompanyList({
  companies, selected, onToggle, onSetAll,
}: {
  companies: CompanyOption[]
  selected: string[]
  onToggle: (symbol: string) => void
  onSetAll: (symbols: string[]) => void
}) {
  const { t, locale } = useT()
  const superadmin = useSuperadmin()
  const allSelected = selected.length === companies.length && companies.length > 0

  return (
    <div className="overflow-hidden rounded-[var(--radius-container)] border border-[var(--border)]">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--bg-sunken)] px-2 py-1">
        {/* An empty selection means "all", so it says so. Rendering
            "13 of 13 selected" above thirteen empty checkboxes is a small
            lie that costs the whole control its credibility. */}
        <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {selected.length === 0
            ? t('filter.allIncluded', { total: companies.length })
            : t('filter.selected', { n: selected.length, total: companies.length })}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className={cn(BULK_ACTION, 'disabled:text-[var(--text-faint)] disabled:no-underline')}
            disabled={allSelected}
            onClick={() => onSetAll(companies.map((c) => c.symbol))}
          >
            {t('action.selectAll')}
          </button>
          <span aria-hidden="true" className="text-[var(--border-strong)]">·</span>
          <button
            type="button"
            className={cn(BULK_ACTION, 'disabled:text-[var(--text-faint)] disabled:no-underline')}
            disabled={selected.length === 0}
            onClick={() => onSetAll([])}
          >
            {t('action.selectNone')}
          </button>
        </div>
      </div>

      <ul className="max-h-[min(19rem,45dvh)] overflow-y-auto overscroll-contain">
        {companies.map((c) => {
          const on = selected.includes(c.symbol)
          return (
            <li key={c.symbol} className="border-b border-[var(--border)] last:border-b-0">
              {/* The <label> is the target, not the box inside it: a 14px
                  checkbox is unhittable, and the row was already the thing
                  people aim at. `tap-row` gives it a floor on a touchscreen
                  and costs nothing with a mouse. */}
              <label
                className={cn(
                  'tap-row flex cursor-default items-center gap-2.5 px-2 py-[5px]',
                  'transition-colors duration-75 active:bg-[var(--accent-soft)]',
                  on ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--bg-sunken)]',
                )}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onToggle(c.symbol)}
                  className="size-4 shrink-0 accent-[var(--accent)]"
                />
                <span className="tnum w-10 shrink-0 font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)] text-[var(--text-muted)]">
                  {c.symbol}
                </span>
                <span className="min-w-0 flex-1 truncate text-[length:var(--text-data)] text-[var(--text)]">
                  {locale === 'ar' && c.nameAr
                    ? <Raw lang="ar">{c.nameAr}</Raw>
                    : (c.nameEn ?? <Raw lang="ar">{c.nameAr}</Raw>)}
                </span>
                <span className="tnum w-16 shrink-0 text-end text-[length:var(--text-micro)] text-[var(--text-faint)]">
                  {t('filter.docsShort', { n: c.documents })}
                </span>
                {/* The pass-rate bar is a verdict, so it is gated with the
                    rest of them. The document count stays: it says how much
                    there is, not how well it scored. */}
                {superadmin && (
                  <>
                    <Bar value={c.passRate} className="shrink-0" />
                    <span
                      className={cn(
                        'tnum w-9 shrink-0 text-end text-[length:var(--text-micro)]',
                        c.passRate === null ? 'text-[var(--text-faint)]'
                          : bandOf(c.passRate) === 'pass' ? 'text-[var(--pass)]'
                          : bandOf(c.passRate) === 'warn' ? 'text-[var(--warn)]'
                          : 'text-[var(--fail)]',
                      )}
                      aria-label={t('filter.passRate')}
                      title={t('filter.passRate')}
                    >
                      {percent0(c.passRate, locale)}
                    </span>
                  </>
                )}
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
